import { useMemo, useState, type FormEvent } from "react";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { errorMessage } from "@/lib/api";
import type { CoachingVisibility } from "@/lib/types";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/components/ui/toast";
import { CoachModal } from "./CoachModal";
import {
  NOTE_CATEGORIES,
  NOTE_CATEGORY_LABELS,
  type ComposerValues,
} from "@/features/coach-review/components/Composer";
import { useMatches } from "@/features/matches/useMatches";
import { matchLabel } from "@/features/coach-review/components/MatchSlotPicker";
import { useQuickFeedback } from "../hooks/useQuickFeedback";
import {
  NO_RECIPIENTS_WARNING,
  sharedWithSentence,
  visibilitySegmentOptions,
} from "../visibilityVocabulary";
import { useRoster } from "../hooks/useRoster";

const TEAM_VALUE = "__team__";
const NO_MATCH_VALUE = "__none__";

/** What the "Add richer evidence in Review" escalation hands back to
 * the shell: the recipient + related match to carry into the URL, and
 * the typed feedback as a composer draft the shell persists so Coach
 * Review's mount-seed (A1) restores it with zero re-typing. */
export type CoachReviewEscalation = {
  playerId?: string;
  matchId?: string;
  draft?: Partial<ComposerValues>;
};

// One vocabulary for every composer — see visibilityVocabulary.ts.
const VISIBILITY_OPTIONS = visibilitySegmentOptions();

/**
 * Resolve a quick-feedback note's title. Prefer the coach's own title; else
 * derive a recognizable one from the key message (so the Feedback Library
 * shows distinct titles, not a wall of identical "Feedback for {player}").
 * The generic per-player string is only the last-resort fallback when nothing
 * was typed.
 */
export function quickFeedbackTitle(input: {
  title: string;
  keyMessage: string;
  playerName?: string;
}): string {
  const typed = input.title.trim();
  if (typed) return typed.slice(0, 120);
  const fromMessage = input.keyMessage.trim().replace(/\s+/g, " ");
  if (fromMessage) {
    return fromMessage.length > 80 ? `${fromMessage.slice(0, 79)}…` : fromMessage;
  }
  return input.playerName ? `Feedback for ${input.playerName}` : "Team feedback";
}

export function QuickFeedbackModal({
  initialPlayerId,
  initialMatchId,
  onClose,
  onOpenCoachReview,
}: {
  initialPlayerId?: string;
  initialMatchId?: string;
  onClose: () => void;
  onOpenCoachReview: (escalation: CoachReviewEscalation) => void;
}) {
  const toast = useToast();
  const rosterQuery = useRoster();
  const matchesQuery = useMatches();
  const mutation = useQuickFeedback();
  const players = rosterQuery.data?.players ?? [];
  const matches = matchesQuery.data ?? [];
  const initialRecipient = initialPlayerId || TEAM_VALUE;
  const [recipient, setRecipient] = useState(initialRecipient);
  const [matchId, setMatchId] = useState(initialMatchId ?? NO_MATCH_VALUE);
  const [title, setTitle] = useState("");
  const [keyMessage, setKeyMessage] = useState("");
  const [nextStep, setNextStep] = useState("");
  const [evidence, setEvidence] = useState("");
  const [category, setCategory] =
    useState<(typeof NOTE_CATEGORIES)[number]>("other");
  const [visibility, setVisibility] = useState<CoachingVisibility>(
    initialPlayerId ? "player" : "team",
  );
  // Whether the coach has picked a visibility themselves. The
  // recipient-driven flip below needs to know INTENT, and the value
  // alone can't carry it: a deliberately-chosen "Whole team" looks
  // exactly like the untouched default. This is the plain-useState
  // equivalent of the `dirtyFields.visibility` guard the note composer
  // uses for the same flip (see Composer.tsx's A4 block).
  const [visibilityTouched, setVisibilityTouched] = useState(false);
  const [coachPrivateNote, setCoachPrivateNote] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedPlayer = useMemo(
    () => players.find((player) => player.id === recipient),
    [players, recipient],
  );
  const playerId = recipient === TEAM_VALUE ? undefined : recipient;
  const relatedMatchId = matchId === NO_MATCH_VALUE ? undefined : matchId;
  const canSubmit = keyMessage.trim().length > 0 && nextStep.trim().length > 0;

  // Mirrors the submit payload: `player_ids` is the single recipient,
  // or empty for a team-wide note.
  const audienceSentence = sharedWithSentence({
    visibility,
    playerIds: playerId ? [playerId] : [],
    players,
  });

  const resolveTitle = () =>
    quickFeedbackTitle({ title, keyMessage, playerName: selectedPlayer?.display_name });

  // The typed feedback as a composer draft, so the "Add richer
  // evidence in Review" escalation carries every field across without
  // the coach re-typing. Mirrors the QuickFeedback → note field map
  // below (key message → summary/what-happened, next step →
  // what-to-do-next, evidence → body).
  const buildDraft = (): Partial<ComposerValues> => ({
    title: resolveTitle(),
    player_summary: keyMessage.trim(),
    what_happened: keyMessage.trim(),
    what_to_do_next: nextStep.trim(),
    body: evidence.trim(),
    coach_private_note: coachPrivateNote.trim(),
    category,
    visibility,
    player_ids: playerId ? [playerId] : [],
  });

  const escalateToReview = () => {
    onOpenCoachReview({
      playerId,
      matchId: relatedMatchId,
      draft: buildDraft(),
    });
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) {
      setError("Add a key message and a next step.");
      return;
    }
    const resolvedTitle = resolveTitle();
    try {
      await mutation.mutateAsync({
        // Observation notes accept an optional related match (the
        // model validator only REQUIRES match_id for video notes); a
        // picked match associates the quick note with that game.
        match_id: relatedMatchId ?? null,
        slot: null,
        timestamp_seconds: null,
        title: resolvedTitle,
        body: evidence.trim(),
        category,
        visibility,
        player_ids: playerId ? [playerId] : [],
        tags: ["quick-feedback"],
        drawing: {},
        note_type: "correction",
        what_happened: keyMessage.trim(),
        why_it_matters: "",
        what_to_do_next: nextStep.trim(),
        player_summary: keyMessage.trim(),
        coach_private_note: coachPrivateNote.trim(),
        note_context: "observation",
        event_title: "",
        event_date: "",
        event_type: "",
        tactical_board_json: null,
      });
      toast.success("Feedback saved");
      onClose();
    } catch (err) {
      const message = errorMessage(err);
      setError(message);
      toast.error("Couldn't save feedback", { description: message });
    }
  };

  return (
    <CoachModal
      title="Give feedback"
      onClose={onClose}
      data-testid="quick-feedback-modal"
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
            Who is this for?
          </span>
          <TkticSelect
            value={recipient}
            onChange={(next) => {
              setRecipient(next);
              // Keep an UNTOUCHED visibility honest about the
              // recipient: picking a named player while still at the
              // `team` default would send that player's feedback to
              // the entire squad, and switching to the whole team
              // can't stay at `player` (it would reach nobody).
              //
              // Gated on `visibilityTouched`, not on the value. A
              // coach who deliberately picks "Whole team" — praising
              // one player's run in front of the squad is a normal
              // thing to want — produces a state indistinguishable
              // from the default, so without the flag their explicit
              // choice was silently rewritten to player-only the
              // moment they named the recipient.
              if (visibilityTouched) return;
              if (next === TEAM_VALUE && visibility === "player") {
                setVisibility("team");
              } else if (next !== TEAM_VALUE && visibility === "team") {
                setVisibility("player");
              }
            }}
            ariaLabel="Feedback recipient"
            data-testid="quick-feedback-recipient"
            options={[
              { value: TEAM_VALUE, label: "Team feedback" },
              ...players.map((player) => ({
                value: player.id,
                label: player.jersey_number
                  ? `#${player.jersey_number} ${player.display_name}`
                  : player.display_name,
              })),
            ]}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
            Title
            <span className="ml-1 normal-case tracking-normal text-ink-muted/70">
              (optional)
            </span>
          </span>
          <input
            type="text"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            maxLength={120}
            data-testid="quick-feedback-title"
            className="rounded border border-rule bg-canvas px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            placeholder={
              keyMessage.trim()
                ? "Defaults to your key message"
                : selectedPlayer
                  ? `Feedback for ${selectedPlayer.display_name}`
                  : "Team feedback"
            }
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
            Key message
          </span>
          <textarea
            value={keyMessage}
            onChange={(event) => setKeyMessage(event.target.value)}
            rows={3}
            data-testid="quick-feedback-message"
            className="min-h-24 rounded border border-rule bg-canvas px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            placeholder="What should the player understand?"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
            What to do next
          </span>
          <textarea
            value={nextStep}
            onChange={(event) => setNextStep(event.target.value)}
            rows={3}
            data-testid="quick-feedback-next-step"
            className="min-h-24 rounded border border-rule bg-canvas px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            placeholder="Name the next action or cue."
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
            Evidence
            <span className="ml-1 normal-case tracking-normal text-ink-muted/70">
              (optional)
            </span>
          </span>
          <textarea
            value={evidence}
            onChange={(event) => setEvidence(event.target.value)}
            rows={2}
            data-testid="quick-feedback-evidence"
            className="min-h-20 rounded border border-rule bg-canvas px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            placeholder="Describe a clip, match moment, note, or observation."
          />
          <button
            type="button"
            onClick={escalateToReview}
            className="mt-2 self-start rounded border border-rule px-3 py-1.5 text-xs font-display uppercase tracking-wider text-ink transition-colors hover:bg-field"
            data-testid="quick-feedback-open-review"
          >
            Add richer evidence in Review
          </button>
        </label>

        <button
          type="button"
          onClick={() => setAdvancedOpen((open) => !open)}
          aria-expanded={advancedOpen}
          className="self-start rounded border border-rule px-3 py-1.5 text-xs font-display uppercase tracking-wider text-ink transition-colors hover:bg-field"
          data-testid="quick-feedback-advanced-toggle"
        >
          Advanced
        </button>

        {advancedOpen ? (
          <div
            className="grid gap-3 rounded border border-rule bg-canvas p-3"
            data-testid="quick-feedback-advanced"
          >
            <label className="flex flex-col gap-1">
              <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
                Category
              </span>
              <TkticSelect
                value={category}
                onChange={(next) =>
                  setCategory(next as (typeof NOTE_CATEGORIES)[number])
                }
                ariaLabel="Feedback category"
                data-testid="quick-feedback-category"
                options={NOTE_CATEGORIES.map((value) => ({
                  value,
                  label: NOTE_CATEGORY_LABELS[value],
                }))}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
                Related match
                <span className="ml-1 normal-case tracking-normal text-ink-muted/70">
                  (optional)
                </span>
              </span>
              <TkticSelect
                value={matchId}
                onChange={setMatchId}
                ariaLabel="Related match"
                data-testid="quick-feedback-match"
                options={[
                  { value: NO_MATCH_VALUE, label: "No related match" },
                  ...matches.map((match) => ({
                    value: match.id,
                    label: matchLabel(match),
                  })),
                ]}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
                Visibility
              </span>
              <SegmentedControl
                value={visibility}
                onChange={(next) => {
                  setVisibilityTouched(true);
                  setVisibility(next as CoachingVisibility);
                }}
                ariaLabel="Feedback visibility"
                data-testid="quick-feedback-visibility"
                options={VISIBILITY_OPTIONS.map((option) => ({
                  ...option,
                  disabled: option.value === "player" && !playerId,
                }))}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
                Coach-only note
              </span>
              <textarea
                value={coachPrivateNote}
                onChange={(event) => setCoachPrivateNote(event.target.value)}
                rows={2}
                data-testid="quick-feedback-private-note"
                className="min-h-20 rounded border border-rule bg-surface px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
                placeholder="Private coaching context."
              />
            </label>
          </div>
        ) : null}

        {error ? (
          <p className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-ink">
            {error}
          </p>
        ) : null}

        {/* Deliberately outside the "Advanced" disclosure: the
          * visibility control is folded away by default, so this is
          * the only thing telling the coach where their feedback is
          * about to go. */}
        <div data-testid="quick-feedback-audience">
          {audienceSentence ? (
            <p className="text-xs text-ink-muted">
              <span className="font-display text-ink">Shared with:</span>{" "}
              {audienceSentence}
            </p>
          ) : (
            <p
              className="rounded-inset border border-flag/40 bg-flag/10 px-2 py-1.5 text-xs text-flag"
              data-testid="quick-feedback-audience-warning"
            >
              {NO_RECIPIENTS_WARNING}
            </p>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-rule pt-3">
          <Button
            type="button"
            onClick={onClose}
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!canSubmit || mutation.isPending}
            data-testid="quick-feedback-submit"
          >
            {mutation.isPending ? "Saving..." : "Save feedback"}
          </Button>
        </div>
      </form>
    </CoachModal>
  );
}
